import React, { cloneElement, isValidElement } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Label } from './Label';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { FieldProps } from './types';

let fieldIdCounter = 0;

function nextFieldId(): string {
  fieldIdCounter += 1;
  return `ds-field-${fieldIdCounter}`;
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 16,
  },
  helper: {
    margin: 0,
  },
}));

export function Field({
  id: idProp,
  label,
  description,
  errorMessage,
  children,
  className,
  disabled,
  error,
  required,
  htmlFor,
  'data-testid': testId,
}: FieldProps) {
  const tokens = useDsTokens();
  const classes = useStyles();
  const [generatedId] = React.useState(() => nextFieldId());
  const fieldId = idProp ?? generatedId;
  const labelId = `${fieldId}-label`;
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const errorId = error && errorMessage ? `${fieldId}-error` : undefined;

  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
  const controlHtmlFor = htmlFor ?? fieldId;

  const control = isValidElement(children)
    ? cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        id: fieldId,
        disabled: disabled ?? (children.props as { disabled?: boolean }).disabled,
        error: error ?? (children.props as { error?: boolean }).error,
        required: required ?? (children.props as { required?: boolean }).required,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-labelledby': label ? labelId : undefined,
      })
    : children;

  return (
    <div className={cn(classes.root, className)} data-testid={testId}>
      {label && (
        <Label
          htmlFor={controlHtmlFor}
          disabled={disabled}
          error={error}
          required={required}
          id={labelId}
        >
          {label}
        </Label>
      )}
      {control}
      {description && !error && (
        <p
          id={descriptionId}
          className={classes.helper}
          style={{
            fontSize: tokens.fontSizeCaption,
            color: tokens.textSecondary,
          }}
        >
          {description}
        </p>
      )}
      {error && errorMessage && (
        <p
          id={errorId}
          className={classes.helper}
          role="alert"
          style={{
            fontSize: tokens.fontSizeCaption,
            color: tokens.statusError,
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

Field.displayName = 'Field';
